import LocalizedLinkButton from 'components/RoundedButton/LocalizedLinkButton';
import React, { Component } from 'react';
import { WithTrekker } from 'utils/analytics';
import { TrekkerType } from '~/types/Analytics';

import { Translator } from '../../Translator';
import CounterControl from './CounterControl';

const CSS = require('./InputNumberOfPeopleSelector.less');

interface InputNumberOfPeopleSelectorProps {
  className?: string;
  maxPeople: number;
  nrOfAdultsInitialValue?: number;
  nrOfChildrenInitialValue?: number;
  onChange?: (nrOfAdults: number, nrOfChildren: number) => void;
  onApply?: () => void;
  trekker: TrekkerType;
  customizationLink?: string;
  hideContactButton?: boolean;
}

interface InputNumberOfPeopleSelectorState {
  nrOfAdults: number;
  nrOfChildren: number;
}

class InputNumberOfPeopleSelector extends Component<
  InputNumberOfPeopleSelectorProps,
  InputNumberOfPeopleSelectorState
> {
  state = {
    nrOfAdults: this.props.nrOfAdultsInitialValue || 4,
    nrOfChildren: this.props.nrOfChildrenInitialValue || 0,
  };

  handleChange = () => {
    this.props.trekker.event('click', {
      component: ['InputNumberOfPeopleSelector'],
      nrOfAdults: this.state.nrOfAdults,
      nrOfChildren: this.state.nrOfChildren,
    });
    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(this.state.nrOfAdults, this.state.nrOfChildren);
    }
  };

  handleApply = () => {
    if (this.props.onApply && typeof this.props.onApply === 'function') {
      this.props.onApply();
    }
  };

  onAdultsIncrease = () => {
    const newNrOfAdults = this.state.nrOfAdults + 1;
    const nrOfChildren = this.state.nrOfChildren || 0;
    if (newNrOfAdults + nrOfChildren <= this.props.maxPeople) {
      this.setState({ nrOfAdults: newNrOfAdults }, this.handleChange);
    }
  };

  onAdultsDecrease = () => {
    const newNrOfAdults = Math.max(this.state.nrOfAdults - 1, 1);
    if (newNrOfAdults !== this.state.nrOfAdults) {
      this.setState({ nrOfAdults: newNrOfAdults }, this.handleChange);
    }
  };

  onChildrenIncrease = () => {
    const newNrOfChildren = this.state.nrOfChildren + 1;
    if (newNrOfChildren + this.state.nrOfAdults <= this.props.maxPeople) {
      this.setState({ nrOfChildren: newNrOfChildren }, this.handleChange);
    }
  };

  onChildrenDecrease = () => {
    const newNrOfChildren = Math.max(this.state.nrOfChildren - 1, 0);
    if (newNrOfChildren !== this.state.nrOfChildren) {
      this.setState({ nrOfChildren: newNrOfChildren }, this.handleChange);
    }
  };

  onContactHostClick = () => {
    this.props.trekker.event('click', {
      component: ['InputNumberOfPeopleSelectorContactHost'],
      buttonText: 'contact-the-host',
    });
  };

  render() {
    return (
      <div className={this.props.className}>
        <CounterControl
          value={this.state.nrOfAdults}
          minValue={1}
          maxValue={this.props.maxPeople - this.state.nrOfChildren}
          onIncrease={this.onAdultsIncrease}
          onDecrease={this.onAdultsDecrease}
        >
          {Translator.translate(
            'Common.SearchInput.nrOfAdults',
            { amount: '' },
            true,
          )}
        </CounterControl>
        <CounterControl
          className={CSS.childrenCounterControl}
          value={this.state.nrOfChildren}
          minValue={0}
          maxValue={this.props.maxPeople - this.state.nrOfAdults}
          onIncrease={this.onChildrenIncrease}
          onDecrease={this.onChildrenDecrease}
        >
          {Translator.translate(
            'Common.SearchInput.nrOfChildren',
            { amount: '' },
            true,
          )}
        </CounterControl>
        <span className={CSS['inputNrOfPeopleSelector-childrenTextWrapper']}>
          <span className={CSS['inputNrOfPeopleSelector-childrenAge']}>
            {Translator.translate(
              'Common.SearchInput.childrenAge',
              { minAge: 0, maxAge: 10 },
              true,
            )}
          </span>
        </span>
        {!this.props.hideContactButton &&
          this.props.customizationLink &&
          this.state.nrOfAdults + this.state.nrOfChildren >=
            this.props.maxPeople && (
            <div className={CSS.MaxPeopleWarning}>
              For groups with more than {this.props.maxPeople} people:{' '}
              <LocalizedLinkButton
                inverted
                onClick={this.onContactHostClick}
                className={CSS.ContactHostLink}
                to={{
                  pathname: this.props.customizationLink,
                  search: `origin=people-selector&adults=${this.state.nrOfAdults}&children=${this.state.nrOfChildren}`,
                }}
                aria-hidden
              >
                Contact the host
              </LocalizedLinkButton>
            </div>
          )}
        {this.props.onApply && (
          <button className={CSS['ApplyButton']} onClick={this.handleApply}>
            {Translator.translate('Common.Apply', {}, true, 'Apply')}
          </button>
        )}
      </div>
    );
  }
}

export default (props) => {
  return (
    <WithTrekker>
      {(trekker) => (
        <InputNumberOfPeopleSelector {...props} trekker={trekker} />
      )}
    </WithTrekker>
  );
};
